import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/** Grille démo dédiée — ≠ mots mêlés de l’utilisateur. */
const DEMO_SIZE = 8;
const DEMO_THEME = 'NATURE';

/** Liste complète des mots placés dans la grille. */
const DEMO_WORDS = [
  { word: 'CHAT', cells: [[0, 0], [0, 1], [0, 2], [0, 3]] },
  { word: 'LUNE', cells: [[2, 1], [3, 1], [4, 1], [5, 1]] },
  { word: 'ROSE', cells: [[6, 2], [5, 3], [4, 4], [3, 5]] },
  { word: 'MER', cells: [[1, 5], [1, 6], [1, 7]] },
  { word: 'BOIS', cells: [[7, 0], [7, 1], [7, 2], [7, 3]] },
  { word: 'EAU', cells: [[3, 7], [4, 7], [5, 7]] },
  { word: 'VENT', cells: [[0, 7], [1, 6], [2, 5], [3, 4]] },
  { word: 'FEU', cells: [[6, 5], [6, 6], [6, 7]] },
];

/** Seuls ces 3 mots sont sélectionnés dans l’animation de glisser. */
const DEMO_ANIMATE = ['CHAT', 'LUNE', 'ROSE'];

/** Mot bonus formé avec les lettres restantes (fin de partie). */
const DEMO_BONUS = 'SAPIN';
/** Cases restantes dans la grille (positions) + lettres affichées dessous (ordre mélangé). */
const DEMO_LEFTOVER = [
  { r: 0, c: 4, letter: 'S' },
  { r: 0, c: 5, letter: 'A' },
  { r: 0, c: 6, letter: 'P' },
  { r: 2, c: 3, letter: 'I' },
  { r: 2, c: 4, letter: 'N' },
];
const DEMO_LEFTOVER_DISPLAY = ['P', 'S', 'N', 'A', 'I'];

function buildDemoGrid() {
  const grid = Array.from({ length: DEMO_SIZE }, () => Array.from({ length: DEMO_SIZE }, () => ''));
  const fill = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const ordered = [
    ...DEMO_WORDS.filter((w) => DEMO_ANIMATE.includes(w.word)),
    ...DEMO_WORDS.filter((w) => !DEMO_ANIMATE.includes(w.word)),
  ];
  ordered.forEach(({ word, cells }) => {
    cells.forEach(([r, c], i) => {
      if (!grid[r][c]) grid[r][c] = word[i];
    });
  });
  DEMO_WORDS.filter((w) => DEMO_ANIMATE.includes(w.word)).forEach(({ word, cells }) => {
    cells.forEach(([r, c], i) => {
      grid[r][c] = word[i];
    });
  });
  DEMO_LEFTOVER.forEach(({ r, c, letter }) => {
    grid[r][c] = letter;
  });
  let n = 0;
  for (let r = 0; r < DEMO_SIZE; r += 1) {
    for (let c = 0; c < DEMO_SIZE; c += 1) {
      if (!grid[r][c]) {
        grid[r][c] = fill[n % fill.length];
        n += 1;
      }
    }
  }
  return grid;
}

function cellKey(r, c) {
  return `${r}-${c}`;
}

function DemoMouse({ leftPressed = false }) {
  return (
    <div className="flex flex-col items-center gap-1.5" aria-hidden>
      <svg viewBox="0 0 72 110" className="w-[6.375rem] h-[9.75rem] drop-shadow-md" role="img">
        <title>Souris</title>
        <rect x="8" y="38" width="56" height="64" rx="28" fill="#e8eaed" stroke="#9aa0a6" strokeWidth="2" />
        <path
          d="M10 38 h24 v28 a4 4 0 0 1 -4 4 H14 a4 4 0 0 1 -4 -4 Z"
          fill={leftPressed ? '#f76f4b' : '#ffffff'}
          stroke="#9aa0a6"
          strokeWidth="2"
        />
        <path
          d="M38 38 h24 v28 a4 4 0 0 1 -4 4 H42 a4 4 0 0 1 -4 -4 Z"
          fill="#ffffff"
          stroke="#9aa0a6"
          strokeWidth="2"
        />
        <rect x="31" y="44" width="10" height="18" rx="5" fill="#bdc1c6" stroke="#9aa0a6" strokeWidth="1.5" />
        <line x1="36" y1="38" x2="36" y2="68" stroke="#9aa0a6" strokeWidth="2" />
      </svg>
      <p className="text-xs text-gray-400 text-center leading-tight max-w-[7rem]">
        {leftPressed ? 'Clic gauche' : 'Souris'}
      </p>
    </div>
  );
}

const KB_ROWS = [
  ['A', 'Z', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['Q', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'M'],
  ['W', 'X', 'C', 'V', 'B', 'N'],
];

function DemoKeyboard({ activeLetter = null, usedLetters = [] }) {
  const usedSet = new Set(usedLetters);
  const keyW = 9.2;
  const keyH = 11;
  const gapX = 1.6;
  const gapY = 2.2;
  const padX = 6;
  const padY = 8;
  const rowWidths = KB_ROWS.map((row) => row.length * keyW + (row.length - 1) * gapX);
  const bodyW = Math.max(...rowWidths) + padX * 2;
  const bodyH = KB_ROWS.length * keyH + (KB_ROWS.length - 1) * gapY + padY * 2;
  const viewW = bodyW + 8;
  const viewH = bodyH + 16;

  const keyFill = (letter) => {
    if (activeLetter === letter) return '#f76f4b';
    if (usedSet.has(letter)) return '#a7f3d0';
    return '#ffffff';
  };
  const keyStroke = (letter) => {
    if (activeLetter === letter) return '#e85f3a';
    if (usedSet.has(letter)) return '#6ee7b7';
    return '#9aa0a6';
  };
  const keyText = (letter) => {
    if (activeLetter === letter) return '#ffffff';
    if (usedSet.has(letter)) return '#065f46';
    return '#5f6368';
  };

  return (
    <div className="flex flex-col items-center gap-1.5" aria-hidden>
      <svg
        viewBox={`0 0 ${viewW} ${viewH}`}
        className="w-[9.5rem] h-auto drop-shadow-md"
        role="img"
      >
        <title>Clavier</title>
        <rect
          x="4"
          y="6"
          width={bodyW}
          height={bodyH}
          rx="10"
          fill="#e8eaed"
          stroke="#9aa0a6"
          strokeWidth="2"
        />
        {KB_ROWS.map((row, ri) => {
          const rowW = rowWidths[ri];
          const startX = 4 + (bodyW - rowW) / 2;
          const y = 6 + padY + ri * (keyH + gapY);
          return row.map((letter, ci) => {
            const x = startX + ci * (keyW + gapX);
            return (
              <g key={letter}>
                <rect
                  x={x}
                  y={y}
                  width={keyW}
                  height={keyH}
                  rx="2.2"
                  fill={keyFill(letter)}
                  stroke={keyStroke(letter)}
                  strokeWidth="1.2"
                />
                <text
                  x={x + keyW / 2}
                  y={y + keyH / 2 + 0.6}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="5.2"
                  fontWeight="700"
                  fontFamily="system-ui, sans-serif"
                  fill={keyText(letter)}
                >
                  {letter}
                </text>
              </g>
            );
          });
        })}
      </svg>
      <p className="text-xs text-gray-400 text-center leading-tight max-w-[7rem]">
        {activeLetter ? `Touche ${activeLetter}` : 'Clavier'}
      </p>
    </div>
  );
}

function CursorArrow() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" className="drop-shadow-lg" aria-hidden>
      <path
        d="M4 3l1 16 5-5 4 8 2-1-4-8 8-1z"
        fill="#111827"
        stroke="#fff"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Démo mots mêlés : 3 mots, curseur qui glisse, souris illustrative à droite.
 */
export default function WordSearchIntroDemo({ onClose, elapsedLabel = '00:00' }) {
  const grid = useMemo(() => buildDemoGrid(), []);
  const [found, setFound] = useState([]);
  const [foundKeys, setFoundKeys] = useState(() => new Set());
  const [hover, setHover] = useState([]);
  const [mouseLeft, setMouseLeft] = useState(false);
  const [cursor, setCursor] = useState(null); // { x, y } viewport
  const [showBonus, setShowBonus] = useState(false);
  const [bonusGuess, setBonusGuess] = useState('');
  const [bonusUsed, setBonusUsed] = useState(() => DEMO_LEFTOVER_DISPLAY.map(() => false));
  const [bonusSolved, setBonusSolved] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [typedKeys, setTypedKeys] = useState([]);
  const wrapRef = useRef(null);
  const gridRef = useRef(null);
  const cursorPosRef = useRef({ x: 0, y: 0 });
  const cancelledRef = useRef(false);
  const [pane, setPane] = useState({ w: 0, h: 0 });
  const leftoverKeys = useMemo(
    () => new Set(DEMO_LEFTOVER.map(({ r, c }) => cellKey(r, c))),
    [],
  );
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const update = () => {
      const dialog = el.closest('[data-game-season-dialog]');
      const scroller = dialog?.querySelector('.overflow-y-auto') || el.closest('.overflow-y-auto');
      const bottom = scroller
        ? scroller.getBoundingClientRect().bottom
        : window.innerHeight - 16;
      const top = el.getBoundingClientRect().top;
      setPane({
        w: Math.max(160, el.clientWidth),
        h: Math.max(160, bottom - top - 16),
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    const timers = [];
    let rafId = 0;

    const wait = (ms) => new Promise((resolve) => {
      const id = window.setTimeout(resolve, ms);
      timers.push(id);
    });

    const cellCenter = (r, c) => {
      const btn = gridRef.current?.querySelector?.(`[data-demo-ws="${r}-${c}"]`);
      if (!btn) return { x: 0, y: 0 };
      const rect = btn.getBoundingClientRect();
      return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    };

    const lerp = (a, b, t) => a + (b - a) * t;
    const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2);

    const setCursorPos = (pos) => {
      cursorPosRef.current = pos;
      setCursor(pos);
    };

    const animateCursor = (from, to, ms) => new Promise((resolve) => {
      const start = performance.now();
      const tick = (now) => {
        if (cancelledRef.current) {
          resolve();
          return;
        }
        const raw = Math.min(1, (now - start) / Math.max(1, ms));
        const t = ease(raw);
        const next = {
          x: lerp(from.x, to.x, t),
          y: lerp(from.y, to.y, t),
        };
        setCursorPos(next);
        if (raw < 1) rafId = requestAnimationFrame(tick);
        else resolve();
      };
      rafId = requestAnimationFrame(tick);
    });

    const selectWord = async (entry) => {
      const cells = entry.cells;
      const start = cellCenter(cells[0][0], cells[0][1]);
      setCursorPos({ x: start.x, y: start.y });
      await wait(200);
      if (cancelledRef.current) return;

      setMouseLeft(true);
      setHover(cells.slice(0, 1).map(([r, c]) => ({ r, c })));
      await wait(180);

      for (let i = 1; i < cells.length; i += 1) {
        if (cancelledRef.current) return;
        const from = cellCenter(cells[i - 1][0], cells[i - 1][1]);
        const to = cellCenter(cells[i][0], cells[i][1]);
        setHover(cells.slice(0, i + 1).map(([r, c]) => ({ r, c })));
        await animateCursor(from, to, 280);
        await wait(40);
      }

      await wait(220);
      if (cancelledRef.current) return;
      setMouseLeft(false);
      setHover([]);
      setFound((prev) => (prev.includes(entry.word) ? prev : [...prev, entry.word]));
      setFoundKeys((prev) => {
        const next = new Set(prev);
        cells.forEach(([r, c]) => next.add(cellKey(r, c)));
        return next;
      });
      await wait(500);
    };

    const markAllWordsFound = () => {
      // Comme la vraie grille : toutes les cases hors lettres restantes sont trouvées
      const nextFound = DEMO_WORDS.map((w) => w.word);
      const nextKeys = new Set();
      for (let r = 0; r < DEMO_SIZE; r += 1) {
        for (let c = 0; c < DEMO_SIZE; c += 1) {
          if (!leftoverKeys.has(cellKey(r, c))) nextKeys.add(cellKey(r, c));
        }
      }
      setFound(nextFound);
      setFoundKeys(nextKeys);
      setHover([]);
      setCursor(null);
      setMouseLeft(false);
      setShowBonus(true);
      setBonusGuess('');
      setBonusUsed(DEMO_LEFTOVER_DISPLAY.map(() => false));
      setBonusSolved(false);
      setActiveKey(null);
      setTypedKeys([]);
    };

    const playBonusPhase = async () => {
      await wait(700);
      if (cancelledRef.current) return;

      const sequence = DEMO_BONUS.split('');
      let guess = '';
      const usedChip = DEMO_LEFTOVER_DISPLAY.map(() => false);

      for (let i = 0; i < sequence.length; i += 1) {
        if (cancelledRef.current) return;
        const letter = sequence[i];
        setActiveKey(letter);
        await wait(280);
        guess += letter;
        setBonusGuess(guess);
        setTypedKeys((prev) => [...prev, letter]);
        const chipIdx = DEMO_LEFTOVER_DISPLAY.findIndex((x, idx) => x === letter && !usedChip[idx]);
        if (chipIdx >= 0) {
          usedChip[chipIdx] = true;
          setBonusUsed([...usedChip]);
        }
        await wait(220);
        setActiveKey(null);
        await wait(160);
      }
      if (cancelledRef.current) return;
      setBonusSolved(true);
      await wait(1800);
    };

    const resetAll = () => {
      setFound([]);
      setFoundKeys(new Set());
      setHover([]);
      setMouseLeft(false);
      setCursor(null);
      setShowBonus(false);
      setBonusGuess('');
      setBonusUsed(DEMO_LEFTOVER_DISPLAY.map(() => false));
      setBonusSolved(false);
      setActiveKey(null);
      setTypedKeys([]);
    };

    const loopOnce = async () => {
      resetAll();
      await wait(500);
      const toAnimate = DEMO_ANIMATE
        .map((w) => DEMO_WORDS.find((entry) => entry.word === w))
        .filter(Boolean);
      for (const entry of toAnimate) {
        if (cancelledRef.current) return;
        await selectWord(entry);
      }
      if (cancelledRef.current) return;
      // Le reste des mots de la liste se trouve (fin de liste), puis phase mot bonus
      await wait(400);
      markAllWordsFound();
      await wait(500);
      await playBonusPhase();
      await wait(400);
    };

    const run = async () => {
      await wait(250);
      while (!cancelledRef.current) {
        await loopOnce();
      }
    };
    run();

    return () => {
      cancelledRef.current = true;
      timers.forEach((id) => window.clearTimeout(id));
      if (rafId) cancelAnimationFrame(rafId);
      setCursor(null);
      setMouseLeft(false);
    };
  }, []);

  const foundSet = useMemo(() => new Set(found), [found]);
  const gap = 2;
  const listReserve = 120;
  const bonusReserve = showBonus ? 150 : 24;
  const twoThirdsW = Math.max(180, Math.floor(pane.w * (2 / 3)));
  const gridAvailW = Math.max(140, twoThirdsW - listReserve - 12);
  const cellFromW = (gridAvailW - gap * (DEMO_SIZE - 1)) / DEMO_SIZE;
  const cellFromH = (Math.max(160, pane.h - bonusReserve) - gap * (DEMO_SIZE - 1)) / DEMO_SIZE;
  const cellPx = Math.max(26, Math.min(56, Math.floor(Math.min(cellFromW, cellFromH) || 32)));
  const fontPx = Math.max(11, Math.round(cellPx * 0.42));
  const gridW = cellPx * DEMO_SIZE + gap * (DEMO_SIZE - 1);

  return (
    <div
      className="relative rounded-md border border-orange-300/60 dark:border-orange-800/60 bg-white/95 dark:bg-gray-950/95 p-3"
      role="dialog"
      aria-label="Démonstration des mots mêlés"
    >
      <div className="relative flex items-center justify-center mb-2 min-h-[2rem]">
        <p className="text-lg font-semibold uppercase tracking-wide text-[#f76f4b]">Démo</p>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 shrink-0 rounded-md bg-[#f76f4b] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#e85f3a]"
        >
          <X size={14} />
          Fermer
        </button>
      </div>

      <p className="text-sm font-mono font-bold text-[#f76f4b] mb-2 tabular-nums">{elapsedLabel}</p>

      <div
        ref={wrapRef}
        className="w-full min-w-0 pointer-events-none select-none grid gap-3 items-stretch"
        style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)' }}
      >
        {/* 2/3 : grille + liste de mots */}
        <div className="min-w-0 flex flex-row flex-wrap sm:flex-nowrap gap-3 items-start justify-center sm:justify-start overflow-hidden">
          <div ref={gridRef} className="shrink-0">
            <div
              className="grid"
              style={{
                gridTemplateColumns: `repeat(${DEMO_SIZE}, ${cellPx}px)`,
                width: gridW,
                gap,
              }}
            >
                {grid.map((row, r) =>
                  row.map((letter, c) => {
                    const hovered = hover.some((cell) => cell.r === r && cell.c === c);
                    const isFound = foundKeys.has(cellKey(r, c));
                    const isLeftover = showBonus && leftoverKeys.has(cellKey(r, c));
                    return (
                      <div
                        key={`${r}-${c}`}
                        data-demo-ws={`${r}-${c}`}
                        className={`font-bold rounded flex items-center justify-center ${
                          hovered
                            ? 'bg-[#f76f4b] text-white'
                            : isLeftover
                              ? 'bg-amber-200 text-amber-950 dark:bg-amber-600 dark:text-amber-50'
                              : isFound
                                ? 'bg-emerald-200 text-emerald-950 dark:bg-emerald-700 dark:text-emerald-50'
                                : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100'
                        }`}
                        style={{ width: cellPx, height: cellPx, fontSize: fontPx }}
                      >
                        {letter}
                      </div>
                    );
                  })
                )}
              </div>
              {showBonus ? (
                <div className="mt-3 space-y-2 w-full" style={{ maxWidth: gridW }}>
                  <p className="text-xs text-gray-700 dark:text-gray-200 font-medium">Lettres restantes</p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    Reformez un mot du thème {DEMO_THEME}.
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {DEMO_LEFTOVER_DISPLAY.map((letter, index) => (
                      <div
                        key={`${letter}-${index}`}
                        className={`w-9 h-9 rounded text-base font-bold flex items-center justify-center ${
                          bonusUsed[index]
                            ? 'bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                            : 'bg-amber-200 text-amber-950 dark:bg-amber-600 dark:text-amber-50'
                        }`}
                      >
                        {letter}
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {DEMO_BONUS.split('').map((letter, index) => {
                      const filled = bonusGuess[index] || '';
                      return (
                        <div
                          key={`slot-${index}`}
                          className={`w-8 h-9 rounded border-2 flex items-center justify-center text-sm font-bold uppercase ${
                            filled
                              ? bonusSolved
                                ? 'border-emerald-400 bg-emerald-100 text-emerald-900 dark:bg-emerald-800 dark:text-emerald-50 dark:border-emerald-500'
                                : 'border-[#f76f4b] bg-orange-50 text-gray-900 dark:bg-orange-950/40 dark:text-white'
                              : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-300'
                          }`}
                        >
                          {filled || '·'}
                        </div>
                      );
                    })}
                  </div>
                  {bonusSolved ? (
                    <p className="text-[11px] text-green-600">Mot du thème trouvé !</p>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="min-w-0 text-sm flex-1 max-w-[9rem]">
              <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-2">
                Thème : <span className="font-semibold text-gray-700 dark:text-gray-200">{DEMO_THEME}</span>
              </p>
              <ul className="text-xs sm:text-sm space-y-1">
                {DEMO_WORDS.map(({ word }) => (
                  <li
                    key={word}
                    className={foundSet.has(word) ? 'line-through text-gray-400' : 'text-gray-700 dark:text-gray-200'}
                  >
                    {word}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        {/* 1/3 : souris puis clavier pour le mot bonus */}
        <div className="min-w-0 flex items-center justify-center self-stretch">
          {showBonus ? (
            <DemoKeyboard activeLetter={activeKey} usedLetters={typedKeys} />
          ) : (
            <DemoMouse leftPressed={mouseLeft} />
          )}
        </div>
      </div>

      {cursor && typeof document !== 'undefined'
        ? createPortal(
          <div
            className="fixed pointer-events-none z-[35000]"
            style={{ left: cursor.x, top: cursor.y, transform: 'translate(-2px, -2px)' }}
          >
            <CursorArrow />
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}
